import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('stepStore');
import { stepsService, Step as ApiStep, StepMetadata as ApiStepMetadata, ApiError } from '../api';

// Type definitions
export interface Step {
  type: string;
  name: string;
  category: string;
  nodeType: string;
  svg: string | null;
  keywords: string[];
  stepType: 'transform' | 'action';
  [key: string]: any; // Allow additional properties
}

export interface StepMetadata {
  name: string;
  category: string;
  type: string;
  nodeType: string;
  svg: string | null;
  keywords: string[];
}

export interface StepStore {
  steps: Step[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  fetchSteps: () => Promise<void>;
  getStepByType: (type: string) => Step | undefined;
  getStepIcon: (type: string) => string | null;
  getStepMetadata: (type: string) => StepMetadata | null;
  getStepByNodeType: (nodeType: string) => Step | undefined;
  getStepIconByNodeType: (nodeType: string) => string | null;
  clearCache: () => void;
}

const useStepStore = create<StepStore>()(
  persist(
    (set, get) => ({
      // State
      steps: [],
      isLoading: false,
      error: null,
      lastFetched: null,

      // Actions
      fetchSteps: async (): Promise<void> => {
        const { lastFetched } = get();

        // Skip if already fetched recently (within 1 hour)
        if (lastFetched && Date.now() - lastFetched < 3600000) {
          logger.debug('Steps already fetched recently, skipping');
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Use the new API service instead of direct fetch
          const steps = await stepsService.getAllSteps();

          set({
            steps,
            isLoading: false,
            error: null,
            lastFetched: Date.now(),
          });

          logger.debug('Steps fetched successfully via API service:', {
            totalCount: steps.length,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : error instanceof Error 
              ? error.message 
              : 'Unknown error';

          logger.error('Failed to fetch steps:', error);
          set({
            isLoading: false,
            error: errorMessage,
          });
        }
      },

      // Selectors - now delegate to the service for consistency
      getStepByType: (type: string): Step | undefined => {
        const { steps } = get();
        return stepsService.getStepByType(steps, type);
      },

      getStepIcon: (type: string): string | null => {
        const { steps } = get();
        return stepsService.getStepIcon(steps, type);
      },

      getStepMetadata: (type: string): StepMetadata | null => {
        const { steps } = get();
        return stepsService.getStepMetadata(steps, type);
      },

      getStepByNodeType: (nodeType: string): Step | undefined => {
        const { steps } = get();
        return stepsService.getStepByNodeType(steps, nodeType);
      },

      getStepIconByNodeType: (nodeType: string): string | null => {
        const { steps } = get();
        return stepsService.getStepIconByNodeType(steps, nodeType);
      },

      // Clear cache (for development/testing)
      clearCache: (): void => {
        set({
          steps: [],
          lastFetched: null,
          error: null,
        });
      },
    }),
    {
      name: 'step-store',
      // Only persist the steps data, not loading states
      partialize: (state) => ({
        steps: state.steps,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

// Export selectors for better performance
export const useSteps = (): Step[] => useStepStore((state) => state.steps);
export const useStepsLoading = (): boolean => useStepStore((state) => state.isLoading);
export const useStepsError = (): string | null => useStepStore((state) => state.error);

export const useStepActions = () =>
  useStepStore((state) => ({
    fetchSteps: state.fetchSteps,
    getStepByType: state.getStepByType,
    getStepIcon: state.getStepIcon,
    getStepMetadata: state.getStepMetadata,
    getStepByNodeType: state.getStepByNodeType,
    getStepIconByNodeType: state.getStepIconByNodeType,
    clearCache: state.clearCache,
  }));

export default useStepStore;
